/** Bounded acquisition context shared by the public offer and authenticated app. */
export const ANALYSIS_SOURCES = [
  "direct",
  "caire",
  "google",
  "bing",
  "chatgpt",
  "partner",
  "email",
  "linkedin",
  "sverigeshemtjanst",
  "hemtjanstguide",
  "nackahemtjanst",
  "hemtjanstistockholm",
  "eirtech",
] as const;
export const ANALYSIS_MEDIA = [
  "direct",
  "organic",
  "referral",
  "email",
  "paid",
  "organic_social",
  "plugin",
] as const;
export const ANALYSIS_LANDINGS = [
  "home",
  "offer",
  "scheduling",
  "routing",
  "analytics",
  "ai_chat",
  "comparison",
  "guide",
  "pricing",
  "persona",
  "help",
  "network",
  "news",
  "other",
] as const;
export const ANALYSIS_CTA_IDS = [
  "analysis_hero",
  "analysis_nav",
  "analysis_offer",
  "analysis_contextual",
  "analysis_footer",
  "analysis_network",
  "analysis_help",
  "analysis_banner",
  "analysis_news",
] as const;

export type AnalysisLocale = "sv" | "en";
export type AnalysisSource = (typeof ANALYSIS_SOURCES)[number];
export type AnalysisMedium = (typeof ANALYSIS_MEDIA)[number];
export type AnalysisLanding = (typeof ANALYSIS_LANDINGS)[number];
export type AnalysisCtaId = (typeof ANALYSIS_CTA_IDS)[number];
export type AnalysisEntryContext = {
  intent: "analysis";
  locale: AnalysisLocale;
  source: AnalysisSource;
  medium: AnalysisMedium;
  landing: AnalysisLanding;
  cta: AnalysisCtaId;
  campaign: "free_analysis";
};

function allowed<T extends string>(
  value: string | null,
  values: readonly T[],
  fallback: T,
): T {
  return values.find((candidate) => candidate === value) ?? fallback;
}

/** Unknown query values are dropped, never persisted as marketing properties. */
export function readAnalysisEntry(
  search: string | URLSearchParams,
): AnalysisEntryContext | null {
  const params =
    typeof search === "string" ? new URLSearchParams(search) : search;
  if (params.get("intent") !== "analysis") return null;
  return {
    intent: "analysis",
    locale: params.get("locale") === "en" ? "en" : "sv",
    source: allowed(params.get("analysis_source"), ANALYSIS_SOURCES, "direct"),
    medium: allowed(params.get("analysis_medium"), ANALYSIS_MEDIA, "direct"),
    landing: allowed(
      params.get("analysis_landing"),
      ANALYSIS_LANDINGS,
      "offer",
    ),
    cta: allowed(
      params.get("analysis_cta"),
      ANALYSIS_CTA_IDS,
      "analysis_offer",
    ),
    campaign: "free_analysis",
  };
}

export function analysisEntryParams(
  context: AnalysisEntryContext,
): URLSearchParams {
  return new URLSearchParams({
    intent: "analysis",
    locale: context.locale,
    analysis_source: context.source,
    analysis_medium: context.medium,
    analysis_landing: context.landing,
    analysis_cta: context.cta,
    analysis_campaign: "free_analysis",
  });
}

/** Only the import route is an analysis return destination; no arbitrary redirect. */
export function analysisImportHref(context: AnalysisEntryContext): string {
  return `/import?${analysisEntryParams(context).toString()}`;
}

/**
 * The free analysis workspace's views a link may open. `summary` is the
 * historical comparison landing; explicit views retain `analysis_view`.
 * Kept equal to the dashboard's `FREE_ANALYSIS_VIEWS` (minus the locked
 * `next`) by a parity test.
 */
export const FREE_ANALYSIS_LANDING_VIEWS = [
  "summary",
  "compare",
  "analysis",
  "source",
  "calendar",
  "plan_reality",
] as const;
export type FreeAnalysisLandingView =
  (typeof FREE_ANALYSIS_LANDING_VIEWS)[number];

/** A unit id as a link may carry it: bounded and opaque, or dropped. */
const LANDING_UNIT_ID = /^[A-Za-z0-9_-]{1,64}$/;

/**
 * The ONE address of a finished free analysis: the workspace for one unit,
 * opened on one view (default Summary of the historical week). "Godkänn och optimera"
 * lands here, and the result email's "Se hela analysen i Caire" links here.
 * The period is not a parameter: a free unit has exactly one accepted
 * period, which the server resolves from the unit. With `appOrigin` the
 * result is absolute (for e-mail); without it, an app path. Signed out, the
 * app's sign-in keeps this path as its return target.
 */
export function freeAnalysisLandingHref(
  input: {
    context: AnalysisEntryContext;
    serviceAreaId?: string | null;
    view?: FreeAnalysisLandingView;
  },
  appOrigin?: string,
): string {
  const params = analysisEntryParams(input.context);
  if (input.serviceAreaId && LANDING_UNIT_ID.test(input.serviceAreaId))
    params.set("analysis_unit", input.serviceAreaId);
  if (input.view) params.set("analysis_view", input.view);
  const path = `/import?${params.toString()}`;
  return appOrigin ? new URL(path, appOrigin).toString() : path;
}

/** Focused public help retains the same bounded analysis handoff. */
export function analysisHelpHref(context: AnalysisEntryContext): string {
  const path = context.locale === "en" ? "/en/help" : "/hjalp";
  return `${path}?${analysisEntryParams(context).toString()}`;
}

export function analysisOfferHref(context: AnalysisEntryContext): string {
  const path =
    context.locale === "en"
      ? "/en/free-schedule-analysis"
      : "/gratis-schemaanalys";
  return `${path}?${analysisEntryParams(context).toString()}`;
}

export function analysisSignupHref(
  appOrigin: string,
  context: AnalysisEntryContext,
): string {
  const url = new URL("/sign-up", appOrigin);
  url.search = analysisEntryParams(context).toString();
  url.searchParams.set("redirect_url", analysisImportHref(context));
  return url.toString();
}

/** Capture only known channel codes from an incoming campaign, never a raw referrer. */
export function createAnalysisEntry(
  locale: AnalysisLocale,
  landing: AnalysisLanding,
  cta: AnalysisCtaId,
  search = "",
): AnalysisEntryContext {
  const params = new URLSearchParams(search);
  const prior = readAnalysisEntry(params);
  return {
    intent: "analysis",
    locale,
    landing,
    cta,
    campaign: "free_analysis",
    source:
      prior?.source ??
      allowed(params.get("utm_source"), ANALYSIS_SOURCES, "direct"),
    medium:
      prior?.medium ??
      allowed(params.get("utm_medium"), ANALYSIS_MEDIA, "direct"),
  };
}
